import { t } from 'i18next';
import { nanoid } from 'nanoid';

import {
  type BuilderView,
  type BuilderViewSourceSchema,
  type BuilderViewSubmitRule,
  type KnackViewType
} from '@/types/schema/BuilderView';
import { type KnackFieldType } from '@/types/schema/KnackField';
import { type KnackObject } from '@/types/schema/KnackObject';
import { type FormView, type FormViewFieldInput } from '@/types/schema/views/FormView';
import { type MenuView } from '@/types/schema/views/MenuView';
import { type RichTextView } from '@/types/schema/views/RichTextView';
import { NEW_VIEW_KEY_PREFIX } from '@/pages/pages/page-editor/helpers/constants';
import { type ViewSource, type ViewSourcePath } from './useViewSources';

type SelectedViewOptions = {
  viewType: KnackViewType;
  viewSource: ViewSource;
  viewSourcePath: ViewSourcePath;
  viewSourcePathDirectConnectionFieldKey?: string;
  viewSourcePathParentConnectionFieldKey?: string;
};

function generateNewViewKey() {
  return `${NEW_VIEW_KEY_PREFIX}${nanoid(10)}` as `new_view_${string}`;
}

function generateViewSourceSchema({
  viewType,
  viewSource,
  viewSourcePath,
  viewSourcePathDirectConnectionFieldKey,
  viewSourcePathParentConnectionFieldKey
}: SelectedViewOptions) {
  const viewSourceSchema: BuilderViewSourceSchema = {
    object: viewSource.object.key,
    criteria: {
      match: 'all',
      rules: [],
      groups: []
    },
    ...(viewSourcePath.isAuthenticatedUser && { authenticated_user: true })
  };

  if (viewSourcePath.connections?.direct.length) {
    // If an explicit direct connection field key is provided, use that connection, otherwise use the first direct connection
    const directConnectionKey =
      viewSourcePathDirectConnectionFieldKey || viewSourcePath.connections.direct[0].field.key;

    const directConnection = viewSourcePath.connections.direct.find(
      (conn) => conn.field.key === directConnectionKey
    );

    if (directConnection) {
      viewSourceSchema.connection_key = directConnectionKey as `field_${string}`;
      // The relationship type is in the context of the source object (e.g. the parent object, or the logged-in user)
      viewSourceSchema.relationship_type = directConnection.relationshipType;
    }
  }

  if (viewSourcePath.connections?.parent.length) {
    // If an explicit parent connection field key is provided, use that connection, otherwise use the first parent connection
    const parentConnectionKey =
      viewSourcePathParentConnectionFieldKey || viewSourcePath.connections.parent[0].field.key;

    const parentConnection = viewSourcePath.connections.parent.find(
      (conn) => conn.field.key === parentConnectionKey
    );

    if (parentConnection) {
      viewSourceSchema.parent_source = {
        connection: parentConnection.field.key as `field_${string}`,
        object: parentConnection.object.key as `object_${string}`
      };
    }
  }

  if (['table', 'list', 'map', 'calendar', 'search'].includes(viewType)) {
    return {
      ...viewSourceSchema,
      limit: '',
      sort: viewSource.object.sort
        ? [
            {
              field: viewSource.object.sort.field as `field_${string}`,
              order: viewSource.object.sort.order
            }
          ]
        : []
    };
  }

  return viewSourceSchema;
}

function generateViewInputs({
  viewType,
  viewSourceObject,
  viewSourceSchema
}: {
  viewType: KnackViewType;
  viewSourceObject: KnackObject;
  viewSourceSchema: BuilderViewSourceSchema;
}) {
  // TODO (FE-2856): generate inputs for other view types
  if (viewType === 'form') {
    const formInputs: FormViewFieldInput[] = [];
    const fieldTypesToIgnore: KnackFieldType[] = [
      'concatenation',
      'auto_increment',
      'sum',
      'min',
      'max',
      'average',
      'count'
    ];

    viewSourceObject.fields.forEach((field) => {
      const isSourceConnectionField = viewSourceSchema.connection_key === field.key;

      if (
        isSourceConnectionField ||
        field.conditional ||
        field.ecommerce ||
        fieldTypesToIgnore.includes(field.type)
      ) {
        return;
      }

      formInputs.push({
        type: field.type,
        label: field.name,
        instructions: '',
        field: {
          key: field.key
        },
        format: field.format
      } as FormViewFieldInput);
    });

    return formInputs;
  }

  return [];
}

function getDefaultSubmitRuleSchema(viewType: KnackViewType) {
  const submitRule: BuilderViewSubmitRule = {
    key: 'submit_1',
    action: 'message',
    message:
      viewType === 'form'
        ? t('views.new_view_defaults.form.submit_rule_message')
        : t('views.new_view_defaults.checkout.submit_rule_message'),
    reload_show: true,
    is_default: true
  };

  return submitRule;
}

function getBaseViewSchema(viewType: KnackViewType) {
  const defaultViewSchema: Partial<BuilderView> = {
    type: viewType,
    description: ''
  };

  // Default view schemas based on the view type
  switch (viewType) {
    case 'rich_text': {
      return {
        ...defaultViewSchema,
        name: t('views.new_view_defaults.rich_text.name'),
        content: `<p>${t('views.new_view_defaults.rich_text.content')}</p>`
      };
    }
    case 'menu': {
      return {
        ...defaultViewSchema,
        name: t('views.new_view_defaults.menu.name'),
        label: t('views.new_view_defaults.menu.label')
      };
    }
    case 'form': {
      return {
        ...defaultViewSchema,
        alert: 'none',
        submitButtonText: t('views.new_view_defaults.form.submit_button_text'),
        rules: {
          submits: [getDefaultSubmitRuleSchema('form')],
          fields: [],
          records: [],
          emails: []
        }
      };
    }
    // TODO: Add more view schemas
    default:
      return defaultViewSchema;
  }
}

export function getNewStaticViewSchema(viewType: KnackViewType) {
  const staticViewSchema = {
    ...getBaseViewSchema(viewType),
    key: generateNewViewKey()
  };

  if (viewType === 'menu') {
    return staticViewSchema as Partial<MenuView>;
  }

  return staticViewSchema as Partial<RichTextView>;
}

export function getNewViewSchema({
  viewType,
  viewSource,
  viewSourcePath,
  viewSourcePathDirectConnectionFieldKey,
  viewSourcePathParentConnectionFieldKey
}: SelectedViewOptions) {
  const baseViewSchema: Partial<BuilderView> = {
    ...getBaseViewSchema(viewType),
    key: generateNewViewKey(),
    name:
      viewSource.recordDisplayQuantity === 'many'
        ? viewSource.object.inflections.plural
        : viewSource.object.inflections.singular
  };

  switch (viewType) {
    case 'form': {
      const formTitle = t(
        viewSource.recordDisplayQuantity === 'many'
          ? 'views.new_view_defaults.form.title_add'
          : 'views.new_view_defaults.form.title_update',
        {
          objectName: viewSource.object.inflections.singular
        }
      );

      const formViewSourceSchema = generateViewSourceSchema({
        viewType: 'form',
        viewSource,
        viewSourcePath,
        viewSourcePathDirectConnectionFieldKey,
        viewSourcePathParentConnectionFieldKey
      });

      const viewFormSchema: Partial<FormView> = {
        action: viewSource.recordDisplayQuantity === 'one' ? 'update' : 'create',
        name: formTitle,
        title: formTitle,
        source: formViewSourceSchema,
        groups: [
          {
            columns: [
              {
                inputs: generateViewInputs({
                  viewType: 'form',
                  viewSourceObject: viewSource.object,
                  viewSourceSchema: formViewSourceSchema
                })
              }
            ]
          }
        ]
      };

      return { ...baseViewSchema, ...viewFormSchema } as FormView;
    }
    default:
      // TODO (FE-2856): Add more view schemas. Look at the `setView` function in v3 at src/store/models/View.js
      // Throwing an error for now to prevent us from adding a new view type without adding the proper schema
      throw new Error(`View type "${viewType}" is not supported.`);
  }
}

export function getNewViewSchemaFromPartialView(
  view: Partial<BuilderView>,
  sourceObject?: KnackObject
) {
  if (!view.type) {
    throw new Error('View type is required.');
  }

  const baseViewSchema = {
    key: generateNewViewKey(),
    ...getBaseViewSchema(view.type),
    ...view
  };

  switch (view.type) {
    case 'form': {
      const formView = view as FormView;

      return {
        ...baseViewSchema,
        groups: [
          {
            columns: [
              {
                inputs: sourceObject
                  ? generateViewInputs({
                      viewType: 'form',
                      viewSourceObject: sourceObject,
                      viewSourceSchema: formView.source
                    })
                  : []
              }
            ]
          }
        ]
      } as FormView;
    }
    default:
      // Throwing an error for now to prevent us from adding a new view type without adding the proper schema
      throw new Error(`View type "${view.type}" is not supported.`);
  }
}
